import { MatterMemberReadSchema } from '@/matter/components/manage-members/schemas'
import { trimAndNullifyEmpty } from '@/util/zod/transforms'
import { parseTimestamp } from '@/util/zod/validators'
import { z } from 'zod'

export enum MatterType {
  STANDARD = 'STANDARD',
  SAMPLE = 'SAMPLE',
  OUTLOOK_WORKSPACE = 'OUTLOOK_WORKSPACE',
  OTHER = 'OTHER',
}

// New backend types must not discard the whole matter list. Non-string payloads still fail.
export const MatterTypeEnum = z.string().pipe(z.nativeEnum(MatterType).catch(MatterType.OTHER))

export enum MatterViewerRole {
  Owner = 'owner',
  Collaborator = 'collaborator',
  Editor = 'editor',
}

export const MatterViewerRoleEnum = z.nativeEnum(MatterViewerRole)

/**
 * A setup banner a matter member can dismiss. Mirrors the backend `MatterBannerKind`.
 *
 * The two drive-sync prompts are separate kinds because they are separate asks: dismissing
 * "connect a provider" must not also silence the later "sync a folder" prompt.
 */
export enum MatterBannerKind {
  Collaboration = 'COLLABORATION',
  DriveSyncConnectFiles = 'DRIVE_SYNC_CONNECT_FILES',
  DriveSyncConnectFolder = 'DRIVE_SYNC_CONNECT_FOLDER',
}

const knownMatterBannerKinds = new Set<string>(Object.values(MatterBannerKind))

/**
 * Dismissed banner kinds, tolerant of a kind a newer backend has added and this build doesn't know.
 * Unknown values are dropped and the known ones kept, so one unrecognized kind can't discard the
 * rest — which would otherwise make every dismissal look like it failed to save. The array itself
 * is still required and still has to be an array.
 */
const DismissedBannerKindsSchema = z.array(z.string()).transform((kinds) => kinds.filter((kind): kind is MatterBannerKind => knownMatterBannerKinds.has(kind)))

export const DismissedMatterBannersResponseSchema = z.object({
  dismissed_banners: DismissedBannerKindsSchema,
})

const nullableTimestamp = z.preprocess((val, ctx) => (val === null ? null : parseTimestamp(val, ctx)), z.number().nullable())

export const MatterReadSchema = z.object({
  id: z.string().uuid(),
  user_id: z.string(),
  name: z.string(),
  client: z.string().nullable(),
  description: z.string().nullable(),
  default_jurisdiction: z.string().nullable(),
  type: MatterTypeEnum,
  created_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  updated_at: z.preprocess((val, ctx) => parseTimestamp(val, ctx), z.number()),
  /**
   * Note: In practice, last_activity_at will always be provided by the backend.
   * However, the field is nullable in the BE schema to account for snapshots of records
   * taken in the past, so we handle the null case for safety.
   */
  last_activity_at: nullableTimestamp,
  // Owner-only create/update responses omit the role; explicit invalid roles must fail.
  // Unknown roles reject the entire matter response. Deploy client support before the backend
  // starts returning a new role, including in list and detail responses.
  viewer_role: MatterViewerRoleEnum.default(MatterViewerRole.Owner),
  member_count: z.number().int().catch(1),
  // Populated only for collaborator rows; null for owner rows
  invited_at: nullableTimestamp.catch(null),
  invited_by_user_id: z.string().nullable().catch(null),
  accepted_at: nullableTimestamp.catch(null),
})

export type MatterRead = z.infer<typeof MatterReadSchema>

// The transfer endpoint always returns membership fields; legacy list fallbacks must not
// leave the former owner with owner controls after a successful transfer.
export const TransferMatterOwnershipResponseSchema = z.object({
  matter: MatterReadSchema.extend({
    viewer_role: MatterViewerRoleEnum,
    member_count: z.number().int(),
    invited_at: z.union([z.string(), z.number(), z.null()]).pipe(nullableTimestamp),
    invited_by_user_id: z.string().nullable(),
    accepted_at: z.union([z.string(), z.number(), z.null()]).pipe(nullableTimestamp),
  }),
})

export type TransferMatterOwnershipResponse = z.infer<typeof TransferMatterOwnershipResponseSchema>

/**
 * MatterRead with nullable fields transformed to undefined for table sorting.
 * This allows TanStack Table's `sortUndefined: 'last'` option to work correctly,
 * keeping blank values at the bottom regardless of sort direction.
 */
export type MatterReadForTable = Omit<MatterRead, 'client' | 'description' | 'last_activity_at'> & {
  client: string | undefined
  description: string | undefined
  last_activity_at: number | undefined
}

export const GetUserMattersResponseSchema = z.object({
  matters: z.array(MatterReadSchema),
})

export type GetUserMattersResponse = z.infer<typeof GetUserMattersResponseSchema>

/**
 * Response from the single-matter detail endpoint: one matter, its members, and the setup banners
 * the requesting user has dismissed on it.
 */
export const DetailedMatterResponseSchema = z.object({
  matter: MatterReadSchema,
  members: z.array(MatterMemberReadSchema),
  // Falls back to an empty set rather than an absent field, so a backend without this still settles
  // `dismissedBannersLoaded` and banners render as they did before.
  dismissed_banners: DismissedBannerKindsSchema.catch([]),
})

export type DetailedMatterResponse = z.infer<typeof DetailedMatterResponseSchema>

export const CreateOrUpdateMatterSchema = z.object({
  // Trim whitespace and validate that name is a non-empty string
  name: z.preprocess(
    (val) => (typeof val === 'string' ? val.trim() : val),
    z.string().min(1, 'This is a required field').max(500, 'Matter name must not exceed 500 characters')
  ),

  // Optional fields: trim whitespace and convert empty strings to null
  client: z.string().nullable().transform(trimAndNullifyEmpty),
  description: z.string().nullable().transform(trimAndNullifyEmpty),
  default_jurisdiction: z.string().nullable().optional(),
})

export type CreateOrUpdateMatterForm = z.infer<typeof CreateOrUpdateMatterSchema>
